import { Component, type ReactNode } from 'react';

/**
 * 화면 하나가 렌더 중 던지면 앱 전체가 하얗게 죽는 문제를 막는다.
 *
 * React 는 에러 경계를 클래스 컴포넌트로만 만들 수 있다 (훅으로 흉내낼 수 없다).
 * resetKey 가 바뀌면(보통 라우트 경로) 다음 화면으로 옮겼을 때 저절로 복구된다 -
 * 안 그러면 한 번 죽은 화면이 다른 메뉴를 눌러도 계속 하얗게 남는다.
 */
interface Props {
  children: ReactNode;
  resetKey: string;
}

interface State {
  error: Error | null;
  resetKey: string;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: unknown, info: { componentStack?: string | null }): void {
    // eslint-disable-next-line no-console
    console.error('화면 렌더 중 오류', error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="state state--error" role="alert">
          <strong>이 화면을 그리다 오류가 났습니다</strong>
          <span>{this.state.error.message}</span>
          <span className="td-muted">다른 메뉴로 이동하면 다시 시도할 수 있습니다.</span>
        </div>
      );
    }
    return this.props.children;
  }
}
